import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import CookieBanner from '../../components/feature/CookieBanner';

const EmpresaPage = () => {
  useEffect(() => {
    document.title = 'Empresa de Pladur en L\'Hospitalet | PLADUREX';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Conoce PLADUREX, empresa especializada en instalación de pladur en L\'Hospitalet de Llobregat. Profesionales con experiencia, materiales de calidad y obra limpia.');
    }
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const workProcess = [
    {
      number: '01',
      title: 'Visita y Medición',
      description: 'Nos desplazamos a tu vivienda, oficina o local para tomar medidas exactas, evaluar el estado actual y entender tus necesidades específicas.',
      icon: 'ri-ruler-line'
    },
    {
      number: '02',
      title: 'Propuesta Detallada',
      description: 'Elaboramos un presupuesto completo con desglose de materiales, mano de obra y plazos. Sin letra pequeña ni sorpresas.',
      icon: 'ri-file-list-3-line'
    },
    {
      number: '03',
      title: 'Ejecución Profesional',
      description: 'Nuestro equipo realiza la instalación con precisión, respetando los plazos acordados y manteniendo el espacio limpio y ordenado.',
      icon: 'ri-tools-line'
    },
    {
      number: '04',
      title: 'Remates y Entrega',
      description: 'Realizamos los acabados finales, limpieza completa y revisión de calidad. Entregamos la obra lista para pintar o decorar.',
      icon: 'ri-checkbox-circle-line'
    }
  ];

  const commitments = [
    {
      icon: 'ri-medal-line',
      title: 'Materiales de Calidad',
      description: 'Trabajamos con placas de pladur de primeras marcas y perfilería certificada para garantizar durabilidad y acabados perfectos.'
    },
    {
      icon: 'ri-broom-line',
      title: 'Limpieza y Orden',
      description: 'Mantenemos el espacio de trabajo limpio durante toda la obra. Al finalizar, dejamos todo recogido y aspirado.'
    },
    {
      icon: 'ri-file-text-line',
      title: 'Facturación Transparente',
      description: 'Emitimos factura detallada de todos nuestros trabajos. Presupuestos claros sin costes ocultos.'
    }
  ];

  return (
    <>
      <Header />
      <WhatsAppButton />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-primary/10 to-primary-light/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <nav className="text-sm text-gray-600 mb-6">
              <Link to="/" className="hover:text-primary cursor-pointer">Inicio</Link>
              <span className="mx-2">&gt;</span>
              <span className="text-primary font-semibold">Empresa</span>
            </nav>
            <h1 className="text-5xl md:text-6xl font-bold text-text-dark mb-6">
              Especialistas en <strong>instalación de pladur</strong> en L'Hospitalet
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Profesionales con experiencia en todo tipo de proyectos de pladur para viviendas, oficinas y locales comerciales
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quiénes Somos */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold text-text-dark mb-6">
                Quiénes somos
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-lg">
                <p>
                  <strong className="text-text-dark">PLADUREX</strong> es una empresa especializada en la <strong>instalación de pladur en L'Hospitalet de Llobregat</strong> y Barcelona. Ofrecemos soluciones completas para la construcción y reforma de espacios interiores mediante sistemas de placa de yeso laminado.
                </p>
                <p>
                  Nuestro equipo cuenta con amplia experiencia en todo tipo de proyectos: desde la instalación de un simple <Link to="/servicios#tabiques" className="text-primary hover:text-primary-dark font-semibold transition-colors">tabique divisorio</Link> hasta reformas integrales con redistribución completa de espacios, <Link to="/servicios#techos" className="text-primary hover:text-primary-dark font-semibold transition-colors">falsos techos registrables</Link> y sistemas de <Link to="/servicios#acustico" className="text-primary hover:text-primary-dark font-semibold transition-colors">aislamiento acústico y térmico</Link>.
                </p>
                <p>
                  Trabajamos tanto en viviendas particulares como en oficinas, locales comerciales y espacios profesionales. Nos adaptamos a las necesidades de cada cliente y proyecto, ofreciendo asesoramiento técnico y soluciones personalizadas.
                </p>
                <p>
                  La calidad de nuestros acabados, el cumplimiento de plazos y la limpieza durante la obra son nuestras señas de identidad. Utilizamos materiales de primeras marcas y aplicamos las mejores técnicas de instalación para garantizar resultados duraderos. ¿Tienes dudas? Consulta nuestras <Link to="/faq" className="text-primary hover:text-primary-dark font-semibold transition-colors">preguntas frecuentes</Link>.
                </p>
              </div>
            </motion.div>

            <motion.div {...fadeInUp}>
              <img
                src="https://public.readdy.ai/ai/img_res/465ad0b3993864152915ad25cf9677b7.jpg"
                alt="Equipo profesional PLADUREX"
                className="w-full h-auto rounded-2xl shadow-xl object-cover"
                style={{ width: '700px', height: '900px' }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cómo Trabajamos */}
      <section className="py-20 bg-bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold text-text-dark mb-4">
              Cómo trabajamos
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proceso transparente y profesional en cada fase del proyecto
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {workProcess.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 relative group"
              >
                <div className="absolute top-6 right-6 text-6xl font-bold text-primary/5 group-hover:text-primary/10 transition-colors">
                  {step.number}
                </div>
                <div className="relative">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                    <i className={`${step.icon} text-3xl text-primary`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-text-dark mb-4">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compromiso */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold text-text-dark mb-4">
              Nuestro compromiso
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Garantizamos calidad, transparencia y profesionalidad en cada proyecto
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {commitments.map((commitment, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center bg-bg-light rounded-2xl p-8"
              >
                <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={commitment.icon} style={{ fontSize: '48px', color: '#ffffff' }}></i>
                </div>
                <h3 className="text-2xl font-bold text-text-dark mb-4">{commitment.title}</h3>
                <p className="text-gray-600 leading-relaxed">{commitment.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trabajamos en Viviendas Ocupadas */}
      <section className="py-20 bg-bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <img
                src="https://readdy.ai/api/search-image?query=clean%20organized%20interior%20construction%20site%20drywall%20installation%20in%20occupied%20home%20minimal%20dust%20protection%20sheets%20careful%20professional%20work%20residential%20renovation%20clean%20workspace%20respectful%20installation%20bright%20natural%20lighting&width=700&height=900&seq=empresa-clean-004&orientation=portrait"
                alt="Trabajo limpio en viviendas ocupadas"
                className="w-full h-auto rounded-2xl shadow-xl object-cover"
                style={{ width: '700px', height: '900px' }}
              />
            </motion.div>

            <motion.div {...fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold text-text-dark mb-6">
                Trabajamos en viviendas y oficinas ocupadas
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-lg">
                <p>
                  Sabemos que no siempre es posible desalojar completamente un espacio durante una obra. Por eso, en <strong className="text-text-dark">PLADUREX</strong> estamos preparados para trabajar en viviendas y oficinas ocupadas, minimizando las molestias.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-check-line text-primary mr-3 mt-1 text-xl flex-shrink-0"></i>
                    <span><strong>Protección de espacios:</strong> Cubrimos muebles y suelos con lonas protectoras</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-primary mr-3 mt-1 text-xl flex-shrink-0"></i>
                    <span><strong>Horarios flexibles:</strong> Nos adaptamos a tus necesidades y horarios</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-primary mr-3 mt-1 text-xl flex-shrink-0"></i>
                    <span><strong>Limpieza diaria:</strong> Recogemos y limpiamos al finalizar cada jornada</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-primary mr-3 mt-1 text-xl flex-shrink-0"></i>
                    <span><strong>Coordinación constante:</strong> Te mantenemos informado del avance de la obra</span>
                  </li>
                </ul>
                <p>
                  La instalación de pladur es una obra relativamente limpia comparada con otros sistemas constructivos, pero aun así tomamos todas las precauciones necesarias para que puedas seguir con tu vida normal durante el proceso.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary-dark">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              ¿Hablamos de tu proyecto?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Contacta con nosotros para una visita sin compromiso. Te asesoramos y preparamos un presupuesto detallado.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+34629501941"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary font-bold text-lg rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-3 text-xl"></i>
                Llamar +34 629 501 941
              </a>
              <Link
                to="/contacto"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-bold text-lg rounded-lg hover:bg-white hover:text-primary transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line mr-3 text-xl"></i>
                Formulario de Contacto
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Enlaces Relacionados */}
      <section className="py-16 bg-bg-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-8" {...fadeInUp}>
            <h2 className="text-3xl font-bold text-text-dark mb-4">
              Explora más
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/servicios"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 group cursor-pointer"
            >
              <i className="ri-service-line text-4xl text-primary mb-3"></i>
              <h3 className="text-lg font-bold text-text-dark mb-2 group-hover:text-primary transition-colors">
                Nuestros Servicios
              </h3>
              <p className="text-sm text-gray-600">
                Tabiques, techos, trasdosados y más
              </p>
            </Link>
            <Link
              to="/faq"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 group cursor-pointer"
            >
              <i className="ri-question-answer-line text-4xl text-primary mb-3"></i>
              <h3 className="text-lg font-bold text-text-dark mb-2 group-hover:text-primary transition-colors">
                Preguntas Frecuentes
              </h3>
              <p className="text-sm text-gray-600">
                Resuelve tus dudas sobre pladur
              </p>
            </Link>
            <Link
              to="/contacto"
              className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 group cursor-pointer"
            >
              <i className="ri-mail-send-line text-4xl text-primary mb-3"></i>
              <h3 className="text-lg font-bold text-text-dark mb-2 group-hover:text-primary transition-colors">
                Contacto
              </h3>
              <p className="text-sm text-gray-600">
                Solicita presupuesto sin compromiso
              </p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <CookieBanner />

      {/* JSON-LD Schema - Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "PLADUREX",
          "url": import.meta.env.VITE_SITE_URL || '',
          "logo": `${import.meta.env.VITE_SITE_URL || ''}/og-image.jpg`,
          "description": "Empresa especializada en instalación de pladur en L'Hospitalet de Llobregat. Profesionales con experiencia en tabiques, falsos techos, trasdosados y aislamiento.",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "L'Hospitalet de Llobregat",
            "addressRegion": "Barcelona",
            "addressCountry": "ES"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+34629501941",
            "contactType": "customer service",
            "availableLanguage": ["es", "ca"]
          },
          "areaServed": [
            {
              "@type": "City",
              "name": "L'Hospitalet de Llobregat"
            },
            {
              "@type": "City",
              "name": "Barcelona"
            }
          ]
        })}
      </script>
    </>
  );
};

export default EmpresaPage;
